import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Captura erros de renderização e mostra o que houve na própria tela.
 *
 * Sem isso, qualquer erro de JS derruba a árvore inteira do React e o
 * usuário vê só uma tela branca — sem pista nenhuma do que aconteceu.
 * Isso é especialmente ruim em celular/tablet, onde não dá pra abrir o
 * console. O userAgent aparece junto porque o print da tela vira o
 * diagnóstico completo.
 */

type Props = { children: ReactNode };
type State = { erro: Error | null; pilhaComponentes: string };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { erro: null, pilhaComponentes: '' };

  static getDerivedStateFromError(erro: Error): Partial<State> {
    return { erro };
  }

  componentDidCatch(erro: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', erro, info.componentStack);
    this.setState({ pilhaComponentes: info.componentStack ?? '' });
    // Manda pro Sentry com a pilha de componentes junto — assim o Vittorio
    // recebe o erro sem depender de a pessoa tirar print. No-op sem DSN.
    Sentry.captureException(erro, {
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  private get relatorio() {
    const { erro, pilhaComponentes } = this.state;
    return [
      `Página: ${window.location.href}`,
      `Navegador: ${navigator.userAgent}`,
      `Erro: ${erro?.name}: ${erro?.message}`,
      '',
      erro?.stack ?? '(sem stack)',
      '',
      '--- componentes ---',
      pilhaComponentes || '(sem pilha)',
    ].join('\n');
  }

  private copiar = async () => {
    try {
      await navigator.clipboard.writeText(this.relatorio);
    } catch {
      // Sem clipboard (navegador antigo ou http): seleciona pro usuário copiar na mão.
      const pre = document.getElementById('erro-relatorio');
      if (pre) {
        const range = document.createRange();
        range.selectNodeContents(pre);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  };

  render() {
    const { erro } = this.state;
    if (!erro) return this.props.children;

    return (
      <div className="container py-4">
        <div className="card border-danger">
          <div className="card-body">
            <h5 className="card-title text-danger mb-3">
              <i className="bi bi-exclamation-triangle me-2" />
              Algo quebrou nesta página
            </h5>

            <p className="text-muted small mb-3">
              Tire um print desta tela e mande pro Vittorio — o texto abaixo diz
              exatamente o que houve.
            </p>

            <pre
              id="erro-relatorio"
              className="bg-body-secondary p-3 rounded small mb-3"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {this.relatorio}
            </pre>

            <div className="d-flex gap-2 flex-wrap">
              <button className="btn btn-primary btn-sm" onClick={this.copiar}>
                <i className="bi bi-clipboard me-1" />
                Copiar
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => window.location.reload()}
              >
                <i className="bi bi-arrow-clockwise me-1" />
                Recarregar
              </button>
              <a className="btn btn-outline-secondary btn-sm" href="/inicio">
                <i className="bi bi-house me-1" />
                Início
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
